import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";
import { FileTextIcon as FileText, ArrowSquareOutIcon as ArrowSquareOut } from "@phosphor-icons/react";

const STATUS_TONE = {
  draft: "bg-[#F1F3F8] text-[#3A4670] border-[#CFD6E8]",
  breeder_signed: "bg-[#FBF7EC] text-[#8A6A00] border-[#E8D9A6]",
  fully_executed: "bg-[#E7F8EC] text-[#0F7B3E] border-[#BFE8C9]",
};

const STATUS_LABEL = {
  draft: "Draft",
  breeder_signed: "Awaiting buyer",
  fully_executed: "Fully executed",
};

export default function AdminContracts() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get("/admin/contracts")
      .then((r) => { setRows(r.data?.contracts || []); setTotal(r.data?.total || 0); })
      .catch(() => setRows([]));
  }, []);

  return (
    <AppShell>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Sale contracts</h1>
            <p className="text-[#6B6B6B] mt-2 max-w-2xl text-[14px] leading-relaxed">
              Every per-puppy sales contract stored on the platform. Armstrong
              is a venue, not a party — we surface these for compliance
              audits and disputes only. Click a row to view the PDF.
            </p>
          </div>
          <span className="text-[12px] text-[#6B6B6B] font-bold uppercase tracking-wider">
            {total} on file
          </span>
        </div>

        <div className="mt-6 rounded-xl border border-[#EAEAEA] bg-white overflow-hidden" data-testid="admin-contracts-table">
          <table className="w-full text-sm">
            <thead className="bg-[#FBFBFB] text-[11px] uppercase tracking-wider text-[#6B6B6B]">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Puppy</th>
                <th className="text-left px-4 py-3 font-bold">Kennel</th>
                <th className="text-left px-4 py-3 font-bold">Buyer</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Uploaded</th>
                <th className="text-left px-4 py-3 font-bold">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F2F2F2]">
              {rows === null && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6B6B6B]">Loading…</td></tr>
              )}
              {rows && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6B6B6B] italic">
                  No contracts on file yet. Breeders upload them from each puppy's detail page.
                </td></tr>
              )}
              {rows && rows.map((r) => (
                <tr key={r.id} data-testid={`admin-contract-row-${r.id}`} className="hover:bg-[#FBFBFB]">
                  <td className="px-4 py-3">
                    <Link to={`/dogs/${r.dog_id}`} className="font-bold text-ink hover:underline inline-flex items-center gap-1">
                      <FileText size={14} weight="duotone" />
                      {r.dog_name}
                    </Link>
                    {r.dog_registration_number && (
                      <p className="text-[11px] text-[#6B6B6B] font-mono">{r.dog_registration_number}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[13px] text-ink">{r.owner_kennel_name || "—"}</span>
                    <p className="text-[11px] text-[#6B6B6B]">{r.owner_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[13px] text-ink">{r.buyer_email || "—"}</span>
                    {r.buyer_signed_at && (
                      <p className="text-[11px] text-[#0F7B3E]">signed {r.buyer_signed_at?.slice(0, 10)}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${STATUS_TONE[r.status] || ""}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-[#6B6B6B]">{r.uploaded_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    {r.pdf_url ? (
                      <a href={r.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-ink underline hover:text-crimson text-[12px]">
                        Open <ArrowSquareOut size={11} />
                      </a>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AppShell>
  );
}
